import { Alert, Platform } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import axios, { InternalAxiosRequestConfig } from 'axios';

import { config as Config } from '../environment';
import { onUnauthorized, readAuthToken } from '../../platform/auth/sessionStore';

// Map to store active requests: key will be Method + URL + Params + Data
const pendingRequests = new Map();

// Helper to generate a unique key for each request
const generateRequestKey = (config: InternalAxiosRequestConfig<any> | undefined) => {
    if (!config) return '';
    const method = (config.method || 'get').toLowerCase();
    const url = config.url || '';
    const params = config.params ? JSON.stringify(config.params) : '';
    let data = '';
    if (config.data) {
        if (typeof config.data === 'string') {
            data = config.data;
        } else {
            try {
                data = JSON.stringify(config.data);
            } catch (e) {
                data = '';
            }
        }
    }
    return `${method}_${url}_${params}_${data}`;
};


// Define a basic instance 
var axiosInstance = axios.create();

// Allows you to run the code or modify the request before the request reaches their destination
axiosInstance.interceptors.request.use(async function (config) {
    const token = await AsyncStorage?.getItem("LoginToken");
    // const token = "U2FsdGVkX1/kSvRRwq3fx/hd5KfAApFU/b23/272S6eQ53D5YOgp6oeB66pnzZF7Hj75Xy4Um/H2X88EmrzWX7PNbFKLbiT4Rk3dgpslTvdlPSmZc2NaO+O84aSLUU81CrYi1s68NXZ/6j25G0oBAas3B5Xoq9SxpzZQiuVB6QVC3Vro2lTP/wfPV+hAt0xB1LXVRCNSDMKbEdg8o9jzFnwRPTIJtb52xK9qblwCLV/Z4S7zVuTDSc/5tV8v/UeQBuCUsAudTI3L7Um4mIww9YDxR3FJXgis3mLqouuQs68epK/o62a0xK8UVT86iiWvR8vlsl0DcFlfP3Qoa27yjF1BsSCu+p4X6DfAX21X8DGJ4jfkH9eEBem6TRX0u6g68/oBB7Whojx60P4j74AmG3qbquNBfjmBxTvEuYghQxfli99rU0++DvPCZ289OB3SXccbWaF0vDI5zCLEOUO9P58xvO3iF1iwih9ByrbDw2H9uaJRd5TxE53EFYyhMC8LnR9sb/Lm97JPHc7yYjOVJd2h4GLGOqSd3HzDXk1Oix1+1frAVTUcVLPfguq4dEBAnZuXFndEvT3F/HKBIOXOpvFDBKEtRVNrCZ/1aeNp18rnkpHG3tQi9VvpGpkjotokg1evXXEeLbUgKafPcXCtLz/CeCdlAdHPqpxwE7HaJr7Cw4R6pEMcBBRbc8JlN+1OJnPo+uF1XcFIfJsMOu23i6jfN8rx3dWDKBrA5KA4OJGuybwwynmcUV88ci9JDJcbRuSbh24aN/YNJF1P8X0OG0no7MHE8p3BQwNY/EEij2Ptza354BlGMuv+WQbeQBLL8JaT3IIxz9I/iYv1lHUAw6jw9G5KgMmPmAVzqQrsZpwyJ9OHYo0AtzOGdjXn996fPxwwlC1BK1Y6pvMbaYVv3fvurOlrHWJoyeXod4T6w2HDaIqYJAaA6Jh2F8FbcUZkyCKegBFV7GOuCZuEDsc+dJIvRf9TtnpsdFUb4Y4uhMikgAhMRzAO2FoHWjnGEgzPA7Wit1tSZ2UtZDJOD+IiC4l2V1BTrah1o3YSALDj0nd1S4AvGJcJ4oxXGw+j2/Y+Io6LAuhvCP9A/eLrbXz2UIlfHWF8L6shQ0WHuoLKXcMH+3msvXjSeXDk2grE8GWjENFmw5j19oh04fr3rhpwyrqpZeMiPkjcyFYuxL0uYglS9wU+Zn1FhNGG+LvBVen0+JsElIYLh8jvni3M6ZnKfQWN3ufTuG8Uow2op5F6jwPZd44yY4VswZ9kkFp1RIgTxseN6yrH4U79mOHhbSt2YHeLPJ2+FQCR5DpBHgNjU/VB5xeqQ44kX2Sed61dcHhMHLhriE6lujCAnvzbtGJlxLG7DjZufIUEYaN7PeRlsGh4sbeiOj9YQNJEgmkARpqMZGCKqFnpvcz/7qddRn71fj+8xBVhTtUI6NrhF7k6qwcLRZQYl5k34lQWwMgli4tLgqapxUSzLcqXPU2ZgoaaXdjLMFKHKx0cRGE5KQW4f9cLX1Cpy7LmUgQ8Sj1ibdyu";

    if (token) {
        // config.headers.Authorization = token;
        config.headers.Authorization = JSON?.parse(token);
    }
    // Browsers own the Origin header and refuse to let scripts set it (and
    // Access-Control-Allow-Origin is a response header), so both are
    // native-only.
    if (Platform.OS !== 'web') {
        config.headers['Access-Control-Allow-Origin'] = '*';
        config.headers['origin'] = Config.origin;
    }

    // Generate a unique key for the request
    const requestKey = generateRequestKey(config);

    // If this exact request is already running, abort the previous one!
    if (pendingRequests.has(requestKey)) {
        console.log(`[HTTP Cancel] Aborting duplicate request: ${config.method?.toUpperCase()} ${config.url}`);
        const oldController = pendingRequests.get(requestKey);
        oldController.abort();
        pendingRequests.delete(requestKey);
    }

    // Create a new controller for the current request
    const controller = new AbortController();
    config.signal = controller.signal;
    pendingRequests.set(requestKey, controller);

    return config;
}, function (error) {
    console.log("interceptor error:", error);
    return Promise.reject(error);
});

axiosInstance.interceptors.response.use(
    (response) => {
        const requestKey = generateRequestKey(response.config);
        pendingRequests.delete(requestKey);
        return response.data;
    },
    (error) => {

        /*
         * 15.12 - 401 and 403 mean different things and are answered
         * differently.
         *
         * 401: the credentials are missing, expired or invalid. The alert
         * below is the existing behaviour and is left alone; what is added is
         * dropping the persisted session context, so the next browser start
         * goes straight to sign-in instead of retrying a dead session on every
         * reload (15.23). onUnauthorized is a no-op on native.
         *
         * 403: authenticated, but not permitted for this resource. It says
         * nothing about the session, so it is passed through to the caller
         * untouched - no alert, no session teardown, and above all no redirect
         * to sign-in. The rejection below still carries the server message so
         * the screen can say what was actually forbidden.
         */
        if (error?.response?.status === 401) {
            onUnauthorized();
            Alert.alert(
                "Session Timeout!",
                "Your session is expired.",
                [{ text: "Ok", style: "cancel" }]
            );
        }

        if (error.config) {
            const requestKey = generateRequestKey(error.config);
            pendingRequests.delete(requestKey);
        }

        if (axios.isCancel(error)) {
            console.log('Duplicate request cancelled automatically:', error.config?.url);
            return new Promise(() => { }); // Stop error propagation to the UI hook
        }
        console.log(error?.response, "---------------");

        if (error?.response?.data) {
            return Promise.reject(error.response.data);
        }

        return Promise.reject({
            success: false,
            message: "Interceptor error",
        });
    }
);

export default axiosInstance;
